/**
 * dcent-counter.js — count-up number animation, triggered on scroll-in.
 *
 * Promotes the EComposer `data-to-value` counter to a native, framework-free,
 * IntersectionObserver-driven component shared across content-rich pages.
 *
 * Markup contract:
 *   <span class="dcent-counter" data-dcent-counter
 *         data-to="4600" data-from="0" data-duration="1600"
 *         data-suffix="+" data-prefix="" data-delimiter=",">0</span>
 *
 * The number animates from data-from to data-to over data-duration ms when the
 * element scrolls into view (once). Respects prefers-reduced-motion (jumps to
 * final). Falls back to the final value when IntersectionObserver/rAF are absent.
 */

export function formatNumber(value, delimiter) {
  const s = String(Math.round(value));
  if (!delimiter) return s;
  return s.replace(/\B(?=(\d{3})+(?!\d))/g, delimiter);
}

export function runCounter(el) {
  if (el.__dcentCounterDone) return;
  el.__dcentCounterDone = true;

  const to = parseFloat(el.dataset.to || "0");
  const from = parseFloat(el.dataset.from || "0");
  const duration = parseFloat(el.dataset.duration || "1600");
  const suffix = el.dataset.suffix || "";
  const prefix = el.dataset.prefix || "";
  const delimiter = el.dataset.delimiter || "";

  const render = (v) => {
    el.textContent = prefix + formatNumber(v, delimiter) + suffix;
  };

  const reduce =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (reduce || typeof requestAnimationFrame === "undefined" || duration <= 0) {
    render(to);
    return;
  }

  let startTime = null;
  const step = (now) => {
    if (startTime === null) startTime = now;
    const t = Math.min(1, (now - startTime) / duration);
    const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
    render(from + (to - from) * eased);
    if (t < 1) requestAnimationFrame(step);
    else render(to);
  };
  requestAnimationFrame(step);
}

export function initCounters(root = document) {
  const els = Array.from(root.querySelectorAll("[data-dcent-counter]"));
  if (!els.length) return;

  if (typeof IntersectionObserver === "undefined") {
    els.forEach(runCounter);
    return;
  }

  const io = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        runCounter(entry.target);
        obs.unobserve(entry.target);
      });
    },
    { threshold: 0.4 },
  );
  els.forEach((el) => io.observe(el));
}

if (typeof window !== "undefined" && typeof document !== "undefined") {
  document.addEventListener("DOMContentLoaded", () => initCounters());
}
